// Jurisdiction (ISO-3166 alpha-2) and source-system display helpers for the
// opportunity surface. Kept tiny and dependency-free — these render in the hot
// per-row path of the Horizon table.

// ISO-2 → flag emoji via Unicode regional-indicator symbols. Works for any
// valid two-letter code (incl. 'EU', which has its own flag). Returns '' for
// anything that isn't two A–Z letters so the caller can fall back to the code.
export function flagEmoji(iso2: string | null | undefined): string {
  if (!iso2) return '';
  const code = iso2.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return '';
  const A = 0x1f1e6;
  return String.fromCodePoint(
    A + (code.charCodeAt(0) - 65),
    A + (code.charCodeAt(1) - 65),
  );
}

// Full country name for the tooltip. Only the jurisdictions we actually ingest
// need to be pretty; anything else falls back to the raw code.
const JURISDICTION_NAME: Record<string, string> = {
  US: 'United States',
  CA: 'Canada',
  GB: 'United Kingdom',
  FR: 'France',
  DE: 'Germany',
  EU: 'European Union',
  AU: 'Australia',
  NZ: 'New Zealand',
  UA: 'Ukraine',
};

export function jurisdictionName(iso2: string | null | undefined): string {
  if (!iso2) return '—';
  const code = iso2.trim().toUpperCase();
  return JURISDICTION_NAME[code] ?? code;
}

// First available product/service classification code across the three schemes
// a tender may carry. OCDS/eForms rows ship CPV as {code, description}; legacy
// rows ship UNSPSC/NAICS as string[] or {code}[]. Returns code + scheme label.
export function firstClassification(row: {
  cpv_codes?: unknown[];
  unspsc_codes?: unknown[];
  naics_codes?: unknown[];
}): { code: string; scheme: string } | null {
  const pick = (arr: unknown[] | undefined): string | null => {
    const first = arr?.[0];
    if (first == null) return null;
    if (typeof first === 'string') return first;
    if (typeof first === 'object' && 'code' in first) {
      const code = (first as { code?: unknown }).code;
      return code == null ? null : String(code);
    }
    return null;
  };
  const cpv = pick(row.cpv_codes);
  if (cpv) return { code: cpv, scheme: 'CPV' };
  const unspsc = pick(row.unspsc_codes);
  if (unspsc) return { code: unspsc, scheme: 'UNSPSC' };
  const naics = pick(row.naics_codes);
  if (naics) return { code: naics, scheme: 'NAICS' };
  return null;
}
